import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageTransitionScreen } from "@/components/LanguageTransitionScreen";
import { useLocale } from "@/hooks/useLocale";

/**
 * Provides a `useLanguageTransition()` API to any descendant. When called,
 * it kicks off the loading-screen sequence and, in parallel, navigates to
 * the target path. The user never sees the target page half-mounted —
 * the overlay covers the transition.
 *
 * Usage in nav / mobile drawer:
 *   const { start } = useLanguageTransition();
 *   <LanguageToggle onTransitionStart={start} />
 */
const LanguageTransitionContext = React.createContext({
  start: () => {},
  isActive: false,
});

export function LanguageTransitionProvider({ children }) {
  const navigate = useNavigate();
  const currentLocale = useLocale();
  const [state, setState] = useState({
    open: false,
    from: "en",
    to: "en",
    settled: false,
  });

  const start = useCallback(
    ({ target, path }) => {
      setState({
        open: true,
        from: currentLocale,
        to: target,
        settled: false,
      });
      // Navigate under the cover of the overlay so the target page can
      // mount and hydrate while the animation plays.
      navigate(path);
    },
    [currentLocale, navigate],
  );

  const dismiss = useCallback(() => {
    setState((s) => (s.open ? { ...s, open: false } : s));
  }, []);

  // Safety: if the overlay is open longer than 4s (should never happen),
  // force-close it so users are never trapped.
  useEffect(() => {
    if (!state.open) return undefined;
    const t = setTimeout(dismiss, 4000);
    return () => clearTimeout(t);
  }, [state.open, dismiss]);

  const ctx = React.useMemo(
    () => ({ start, isActive: state.open }),
    [start, state.open],
  );

  return (
    <LanguageTransitionContext.Provider value={ctx}>
      {children}
      <LanguageTransitionScreen
        open={state.open}
        onDismiss={dismiss}
        fromLocale={state.from}
        toLocale={state.to}
      />
    </LanguageTransitionContext.Provider>
  );
}

export function useLanguageTransition() {
  return React.useContext(LanguageTransitionContext);
}

export default LanguageTransitionProvider;
